import React from 'react'
import { FormikProvider } from 'formik'

import { useHistory } from 'react-router-dom'
import _ from 'lodash'
import TextFieldAdornment from 'molecules/TextFieldAdornment'
import useVehicleSlider from './useVehicleSlider'

import Label from 'atoms/Label'

import { auxiliary_load_options } from 'pages/Vehicle/helpers/hv_options'

import Typography from 'atoms/Typography'
import RangeField from 'atoms/RangeField'
import RangeArrayField from 'atoms/RangeArrayField'
import default_bus_image from 'assets/bus_images/heavy_vehicle_image.jpg'

import ArrowUp from 'assets/images/arrow-top.svg'
import ArrowDown from 'assets/images/arrow-down.svg'

import {
  StyledCard,
  StyledCardHeader,
  StyledUl,
  StyledLi,
  StyledSliderContainer,
  StyledLabel,
  StyledColumnContainer,
  StyledLinkButton,
  StyledCardBody,
  StyledImageContainer,
  StyledContainer,
  StyledInnerContainer,
  StyledSettingInnerContainer,
  StyledRangeContainer,
  StyledToggleButton,
  StyledInnerHeader,
  StyledInnerBody,
  StyledToggleButtonInner,
  StyledTooltipTextGroup,
  StyledTooltipText,
} from './VehicleSliderStyle'

import ArrowIcon from 'assets/images/SvgComponents/ChevronIcon'

const VehicleSlider = ({ setSettings, selectedVehicleData, regionData }: any) => {
  const history = useHistory()
  const { formik, vehicleData, weekFirstCharacters, set_show_settings, show_settings } =
    useVehicleSlider({
      setSettings,
      selectedVehicleData,
      regionData,
    })

  const auxuliary_load_label = _.find(auxiliary_load_options, {
    value: vehicleData?.auxiliary_load,
  })?.label

  const fuel_tooltip = (
    <StyledTooltipText>
      This is the fuel cost used to calculate operating cost for the diesel vehicle over the entire
      operating period. You can modify this cost based on the desired assumption of costs going up
      or down
    </StyledTooltipText>
  )

  const electricity_tooltip = (
    <StyledTooltipTextGroup>
      <StyledTooltipText className="padding_after">
        This is the electricity energy rates used to calculate operating cost for the electric vehicle over
        the entire operating period. You can modify this cost based on the desired assumption of
        costs going up or down. This price does not include demand or other charges nor does it consider local renewable 
        generation like solar which might also influence total electricity cost
      </StyledTooltipText>
      <StyledTooltipText className="padding_after">
        The percentage of eV charging indicates the band of use which determines the electricity
        cost to be considered for operational costs.
      </StyledTooltipText>
      <StyledTooltipText className="padding_after">
        Percentage of green power is a measure of renewable energy use and impact CO2 emissions for
        the electricity use
      </StyledTooltipText>
    </StyledTooltipTextGroup>
  )

  const general_tooltip = (
    <StyledTooltipTextGroup>
      <StyledTooltipText className="padding_after">
        Operating period indicates the number of years that you operate the vehicle
      </StyledTooltipText>
      <StyledTooltipText className="padding_after">
        Battery replacement is the replacement timeframe for the electric vehicle battery. Typically
        once in 8 years
      </StyledTooltipText>
      <StyledTooltipText className="padding_after">
        Current and replacement vehicle depreciation sliders help you understand vehicle depreciated
        value based on expected deprecation for the vehicle(s)
      </StyledTooltipText>
    </StyledTooltipTextGroup>
  )

  const onHandleShowHide = (
    value: 'fuel' | 'vehicle_info' | 'settings' | 'electricity' | 'general',
  ) => {
    set_show_settings((prevSettings) => ({
      ...prevSettings,
      [value]: !prevSettings[value],
    }))
  }

  const { vehicle_info, settings, electricity, fuel, general } = show_settings
  return (
    <>
      <StyledCard>
        <StyledCardHeader onClick={() => onHandleShowHide('vehicle_info')}>
          <Typography variant="h4" weight={600} color="#fff">
            Vehicle
          </Typography>
          <StyledToggleButton>
            <img src={vehicle_info ? ArrowUp : ArrowDown} alt="arrow" />
          </StyledToggleButton>
        </StyledCardHeader>
        <StyledCardBody show={vehicle_info}>
          <StyledInnerContainer>
            <StyledImageContainer>
              <img src={default_bus_image} alt="bus" />
            </StyledImageContainer>
            <StyledUl>
              <StyledLi>
                <span>Weight class</span>
                <span>{vehicleData?.heavy_vehicle_weight_class}</span>
              </StyledLi>
              <StyledLi>
                <span>Replacement vehicle</span>
                <span>
                  {vehicleData?.make} {vehicleData?.model}
                </span>
              </StyledLi>
              <StyledLi>
                <span>Distance per day</span>
                <span>{vehicleData?.distance_travelled_per_day} km</span>
              </StyledLi>

              <StyledLi>
                <span>Days of operation</span>
                <span>{weekFirstCharacters}</span>
              </StyledLi>

              <StyledLi>
                <span>Payload</span>
                <span>{vehicleData?.vehicle_payload}</span>
              </StyledLi>

              <StyledLi>
                <span>Auxiliary load</span>
                <span>{auxuliary_load_label}</span>
              </StyledLi>

              <StyledLi>
                <span>Terrain</span>
                <span>{vehicleData?.terrain}</span>
              </StyledLi>
            </StyledUl>
            <StyledLinkButton onClick={() => history.goBack()}>Edit inputs</StyledLinkButton>
          </StyledInnerContainer>
        </StyledCardBody>
      </StyledCard>

      <FormikProvider value={formik}>
        <StyledContainer>
          <StyledCard>
            <StyledCardHeader onClick={() => onHandleShowHide('settings')}>
              <Typography variant="h4" weight={600} color="#fff">
                Settings
              </Typography>
              <StyledToggleButton>
                <img src={settings ? ArrowUp : ArrowDown} alt="arrow" />
              </StyledToggleButton>
            </StyledCardHeader>
            <StyledCardBody show={settings}>
              <StyledSettingInnerContainer>
                <StyledSliderContainer>
                  <StyledInnerHeader>
                    <Typography
                      variant="h4"
                      weight={600}
                      tooltip={fuel_tooltip}
                      tooltipId="fuel_info"
                    >
                      Fuel
                    </Typography>
                    <StyledToggleButtonInner onClick={() => onHandleShowHide('fuel')} show={fuel}>
                      <ArrowIcon />
                    </StyledToggleButtonInner>
                  </StyledInnerHeader>
                  <StyledInnerBody show={fuel}>
                    <StyledColumnContainer isPadding>
                      <Label color="black">Diesel + Adblue price</Label>
                      <TextFieldAdornment
                        numeric
                        adornment="A$/L"
                        name="price_of_diesel"
                        disabled
                      />
                    </StyledColumnContainer>
                    <RangeField name="price_of_diesel" min={1} max={10} step={0.01} />
                  </StyledInnerBody>
                </StyledSliderContainer>

                <StyledSliderContainer>
                  <StyledInnerHeader>
                    <Typography
                      variant="h4"
                      weight={600}
                      tooltip={electricity_tooltip}
                      tooltipId="electricity_info"
                    >
                      Electricity
                    </Typography>
                    <StyledToggleButtonInner
                      onClick={() => onHandleShowHide('electricity')}
                      show={electricity}
                    >
                      <ArrowIcon />
                    </StyledToggleButtonInner>
                  </StyledInnerHeader>
                  <StyledInnerBody show={electricity}>
                    <StyledRangeContainer>
                      <div>
                        <StyledColumnContainer isPadding>
                          <Label color="black">Peak rate</Label>
                          <TextFieldAdornment
                            disabled
                            numeric
                            adornment="A$/kWh"
                            name="peak_rate"
                          />
                        </StyledColumnContainer>
                        <RangeField name="peak_rate" min={0} max={1} step={0.01} />
                      </div>
                      <div>
                        <StyledColumnContainer isPadding>
                          <Label color="black">Off-peak rate</Label>
                          <TextFieldAdornment
                            disabled
                            numeric
                            adornment="A$/kWh"
                            name="off_peak_rate"
                          />
                        </StyledColumnContainer>
                        <RangeField name="off_peak_rate" min={0} max={1} step={0.01} />
                      </div>
                      <div>
                        <StyledColumnContainer isPadding>
                          <Label color="black">Shoulder rate</Label>
                          <TextFieldAdornment
                            disabled
                            numeric
                            adornment="A$/kWh"
                            name="shoulder_rate"
                          />
                        </StyledColumnContainer>
                        <RangeField name="shoulder_rate" min={0} max={1} step={0.01} />
                      </div>
                      <div>
                        <StyledLabel mb={21} color="#333333" weight={400}>
                          Baseline % EV Charging in each rate band
                        </StyledLabel>
                        <RangeArrayField
                          name="ev_charging"
                          labels={['Peak', 'Shoulder', 'OffPeak']}
                        />
                      </div>
                      <div>
                        <RangeField
                          name="electricity_green_percent"
                          label="Percentage Green Power"
                          unit="%"
                          min={0}
                          max={100}
                        />
                      </div>
                    </StyledRangeContainer>
                  </StyledInnerBody>
                </StyledSliderContainer>

                <StyledSliderContainer>
                  <StyledInnerHeader>
                    <Typography
                      variant="h4"
                      weight={600}
                      tooltip={general_tooltip}
                      tooltipId="general_info"
                      mb={general ? 14 : 0}
                    >
                      General
                    </Typography>
                    <StyledToggleButtonInner
                      onClick={() => onHandleShowHide('general')}
                      show={general}
                    >
                      <ArrowIcon />
                    </StyledToggleButtonInner>
                  </StyledInnerHeader>
                  <StyledInnerBody show={general}>
                    <StyledRangeContainer>
                      <div>
                        <StyledColumnContainer>
                          <Label color="black">Operating period</Label>
                          <TextFieldAdornment
                            disabled
                            numeric
                            adornmentRight={'years'}
                            name="operating_period"
                          />
                        </StyledColumnContainer>
                        <RangeField name="operating_period" min={2} max={25} />
                      </div>

                      <div>
                        <StyledColumnContainer>
                          <Label color="black">Battery replacement</Label>
                          <TextFieldAdornment
                            disabled
                            numeric
                            adornmentRight={'years'}
                            name="battery_replacement_period"
                          />
                        </StyledColumnContainer>
                        <RangeField
                          name="battery_replacement_period"
                          min={5}
                          max={15}
                          // max={maxForReplacementPeriod}
                        />
                      </div>
                      <div>
                        <StyledColumnContainer>
                          <Label color="black">Current vehicle yearly depreciation</Label>
                          <TextFieldAdornment
                            disabled
                            numeric
                            adornmentRight="%"
                            name="diesel_bus_yearly_depreciation"
                          />
                        </StyledColumnContainer>
                        <RangeField
                          name="diesel_bus_yearly_depreciation"
                          min={5}
                          max={20}
                          step={0.01}
                        />
                      </div>
                      <div>
                        <StyledColumnContainer>
                          <Label color="black">Electric vehicle yearly depreciation</Label>
                          <TextFieldAdornment
                            disabled
                            numeric
                            adornmentRight="%"
                            name="electric_bus_yearly_depreciation"
                          />
                        </StyledColumnContainer>
                        <RangeField
                          name="electric_bus_yearly_depreciation"
                          min={5}
                          max={20}
                          step={0.01}
                        />
                      </div>
                    </StyledRangeContainer>
                  </StyledInnerBody>
                </StyledSliderContainer>
              </StyledSettingInnerContainer>
            </StyledCardBody>
          </StyledCard>
        </StyledContainer>
      </FormikProvider>
    </>
  )
}

export default VehicleSlider
